import { AudioQualityIndicator } from '@/components/session/AudioQualityIndicator';
import { AudioVisualizer } from '@/components/session/audio-visualizer';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { TutorialAnchor } from '@/features/session-tutorial/TutorialAnchor';
import { useTutorialStepAction } from '@/features/session-tutorial/useTutorialStepAction';
import { formatTime } from '@/lib/sessionUtils';
import { cn } from '@/lib/utils';
import type { AudioQualityDisplay } from '@/types/audioQuality';
import { Check, ChevronDown, Eye, Loader2, Mic, Pause } from 'lucide-react';

interface MicDropdownProps {
  devices: MediaDeviceInfo[];
  selectedDeviceId: string;
  onDeviceChange?: (deviceId: string) => void;
  align?: 'start' | 'end';
  size?: 'sm' | 'default';
}

export const MicDropdown = ({
  devices,
  selectedDeviceId,
  onDeviceChange,
  align = 'end',
  size = 'default',
}: MicDropdownProps) => (
  <PopoverContent align={align} className={size === 'sm' ? 'w-64 p-1' : 'w-72 p-1'}>
    {size === 'default' && <p className="px-2.5 py-1.5 font-medium text-muted-foreground text-xs">Veldu hljóðnema</p>}
    <div className="space-y-0.5">
      {devices.map((device) => (
        <button
          type="button"
          key={device.deviceId}
          className={cn(
            'flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left transition-colors hover:bg-muted',
            size === 'sm' ? 'text-xs' : 'text-sm',
            device.deviceId === selectedDeviceId && 'bg-muted font-medium'
          )}
          onClick={() => onDeviceChange?.(device.deviceId)}
        >
          <Mic className={cn('shrink-0 text-muted-foreground', size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4')} />
          <span className="min-w-0 truncate">{device.label || `Hljóðnemi ${device.deviceId.slice(0, 5)}`}</span>
          {device.deviceId === selectedDeviceId && (
            <Check className={cn('ml-auto shrink-0 text-primary', size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4')} />
          )}
        </button>
      ))}
    </div>
  </PopoverContent>
);

interface RecordingControlsProps {
  isRecording: boolean;
  isPaused: boolean;
  isInitializing: boolean;
  isFinalizingTranscription: boolean;
  elapsedTime: number;
  onToggleRecording: () => void;
  selectedDeviceId: string;

  // Mic selector props
  devices?: MediaDeviceInfo[];
  onDeviceChange?: (deviceId: string) => void;

  // Optional states
  isSmartReviewing?: boolean;
  isViewingAsDelegate?: boolean;
  disabled?: boolean;
  showMobileRecordingButton?: boolean;
  audioQuality?: AudioQualityDisplay | null;
}

export const RecordingControls = ({
  isRecording,
  isPaused,
  isInitializing,
  isFinalizingTranscription,
  elapsedTime,
  onToggleRecording,
  selectedDeviceId,
  devices = [],
  onDeviceChange,
  isSmartReviewing = false,
  isViewingAsDelegate = false,
  disabled = false,
  showMobileRecordingButton = true,
  audioQuality,
}: RecordingControlsProps) => {
  const isProcessing = isInitializing || isFinalizingTranscription || isSmartReviewing;
  const handleRecordClick = useTutorialStepAction('record', onToggleRecording);
  const isButtonDisabled = disabled || isProcessing || isViewingAsDelegate;
  const disabledTitle = isViewingAsDelegate ? 'Ekki hægt að taka upp á vegum annars notanda' : undefined;
  const hasMicSelector = devices.length > 0 && !!onDeviceChange;

  const selectedDevice = devices.find((d) => d.deviceId === selectedDeviceId);
  const deviceLabel = selectedDevice?.label || 'Hljóðnemi';
  const shortLabel = deviceLabel.length > 20 ? `${deviceLabel.slice(0, 18)}…` : deviceLabel;

  const getButtonColorClass = () => {
    if (isProcessing) return 'bg-slate-400 hover:bg-slate-500';
    if (isRecording && !isPaused) return 'bg-red-500 hover:bg-red-600';
    if (isPaused) return 'bg-amber-500 hover:bg-amber-600';
    return 'bg-primary hover:bg-primary/90';
  };

  const getTimerClasses = () => {
    if (isRecording && !isPaused) return 'border-red-200 bg-red-50 text-red-600';
    if (isPaused) return 'border-amber-200 bg-amber-50 text-amber-600';
    return 'border-slate-200 bg-slate-50 text-slate-600';
  };

  const getTimerDotClass = () => {
    if (isRecording && !isPaused) return 'animate-pulse bg-red-500';
    if (isPaused) return 'bg-amber-500';
    return 'bg-slate-400';
  };

  return (
    <>
      {/* Mobile recording controls */}
      <div className="flex shrink-0 items-center gap-2 md:hidden">
        {/* Mobile mic picker */}
        {hasMicSelector && (
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="inline-flex h-9 items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 shadow-sm hover:bg-slate-50"
              >
                <Mic className="h-3 w-3" />
                <ChevronDown className="h-3 w-3" />
              </button>
            </PopoverTrigger>
            <MicDropdown
              devices={devices}
              selectedDeviceId={selectedDeviceId}
              onDeviceChange={onDeviceChange}
              align="start"
              size="sm"
            />
          </Popover>
        )}

        {/* Mobile Timer + Record Button */}
        {showMobileRecordingButton && (
          <Button
            data-testid="mobile-recording-button"
            disabled={isButtonDisabled}
            className={`flex h-14 w-30 items-center gap-1.5 rounded-full border px-2.5 ${getTimerClasses()} ${isButtonDisabled ? 'cursor-not-allowed opacity-50' : ''}`}
            onClick={onToggleRecording}
            title={disabledTitle}
          >
            <div className={`h-1.5 w-1.5 rounded-full ${getTimerDotClass()}`} />
            <span className="font-medium font-mono text-[11px]">{formatTime(elapsedTime)}</span>

            {isProcessing ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : isRecording && !isPaused ? (
              <Pause className="h-3.5 w-3.5 fill-current" />
            ) : (
              <Mic className="h-3.5 w-3.5" />
            )}
          </Button>
        )}
      </div>

      {/* Desktop recording controls */}
      <div className="hidden items-center gap-3 md:flex">
        {isViewingAsDelegate && (
          <div className="flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3 py-1.5 text-blue-700 text-xs">
            <Eye className="h-3 w-3" />
            <span>Ekki hægt að taka upp á vegum annars notanda</span>
          </div>
        )}

        {isSmartReviewing && <span className="text-primary text-xs">Les yfir talgreiningu...</span>}

        {/* Mic selector pill */}
        {hasMicSelector ? (
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="inline-flex h-10 items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 text-xs shadow-sm hover:bg-slate-50"
              >
                <Mic className="h-3.5 w-3.5 shrink-0" />
                <span className="max-w-[140px] truncate">{shortLabel}</span>
                <ChevronDown className="h-3 w-3 shrink-0 text-slate-400" />
              </button>
            </PopoverTrigger>
            <MicDropdown devices={devices} selectedDeviceId={selectedDeviceId} onDeviceChange={onDeviceChange} />
          </Popover>
        ) : (
          <span className="text-slate-400 text-xs">Enginn hljóðnemi</span>
        )}

        {/* Record button */}
        <TutorialAnchor step="record">
          <Button
            size="lg"
            disabled={isButtonDisabled}
            className={`h-12 w-12 rounded-full shadow-lg transition-all ${getButtonColorClass()} ${isViewingAsDelegate ? 'cursor-not-allowed opacity-50' : ''}`}
            onClick={handleRecordClick}
            title={disabledTitle}
          >
            {isProcessing ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : isRecording && !isPaused ? (
              <Pause className="h-5 w-5 fill-current" />
            ) : (
              <Mic className="h-5 w-5" />
            )}
          </Button>
        </TutorialAnchor>

        <AudioVisualizer isRecording={isRecording} deviceId={selectedDeviceId} />
      </div>
      {audioQuality ? <AudioQualityIndicator quality={audioQuality} /> : null}
    </>
  );
};
